
import React from "react";
import { ExternalLink, Facebook, Twitter, Instagram, Youtube, Linkedin, Link as LinkIcon } from "lucide-react";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger 
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";

interface CustomLink {
  label: string;
  url: string;
  icon?: string;
}

interface ProfileSocialLinksProps {
  profile: {
    custom_links?: CustomLink[];
  };
}

const SOCIAL_ICONS: { [key: string]: React.ComponentType } = {
  facebook: Facebook,
  twitter: Twitter,
  instagram: Instagram,
  youtube: Youtube,
  linkedin: Linkedin,
};

const ProfileSocialLinks: React.FC<ProfileSocialLinksProps> = ({ profile }) => {
  console.log("ProfileSocialLinks - Received profile:", profile);
  
  // Validate profile object
  if (!profile || typeof profile !== 'object') {
    console.warn("ProfileSocialLinks - Invalid profile object");
    return null;
  }

  // Validate and filter custom_links
  if (!profile.custom_links || !Array.isArray(profile.custom_links)) {
    console.log("ProfileSocialLinks - No valid custom_links array found");
    return null;
  }

  // Filter out empty or invalid links with additional validation
  const customLinks = profile.custom_links.filter((link: any) => {
    if (!link || typeof link !== 'object') {
      console.warn("ProfileSocialLinks - Invalid link object:", link);
      return false;
    }
    
    if (!link.label || typeof link.label !== 'string' || link.label.trim() === '') {
      console.warn("ProfileSocialLinks - Invalid or empty label:", link);
      return false;
    }
    
    if (!link.url || typeof link.url !== 'string' || link.url.trim() === '') {
      console.warn("ProfileSocialLinks - Invalid or empty URL:", link);
      return false;
    }
    
    return true;
  });

  if (customLinks.length === 0) {
    console.log("ProfileSocialLinks - No valid custom links found after filtering");
    return null;
  }

  console.log("ProfileSocialLinks - Rendering valid custom links:", customLinks);

  const getIconComponent = (iconName: string) => {
    if (!iconName) return LinkIcon;
    const IconComponent = SOCIAL_ICONS[iconName.toLowerCase()];
    return IconComponent || LinkIcon;
  };

  return (
    <div>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" className="w-full">
            <LinkIcon className="h-4 w-4 mr-2" />
            Social Links
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-56 bg-background">
          {customLinks.map((link, index) => {
            try {
              const IconComponent = getIconComponent(link.icon);
              
              return (
                <DropdownMenuItem key={`${link.label}-${index}`} asChild>
                  <a 
                    href={link.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center justify-between w-full"
                  >
                    <div className="flex items-center gap-2">
                      <IconComponent className="h-4 w-4" />
                      <span>{link.label}</span>
                    </div>
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </DropdownMenuItem>
              );
            } catch (error) {
              console.error("ProfileSocialLinks - Error rendering link:", link, error);
              return null;
            }
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export default ProfileSocialLinks;
